import { useTranslation } from 'react-i18next';
import { History } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { formatFlightTime } from '@/lib/disruptions/airport-tz';
import { useCareHistory } from '@/lib/disruptions/use-disruptions';
import { formatMinor } from '@/routes/disruptions/care-desk/care-desk-model';
import { actorLabel, entryTitle, entryWhy, signedAmount } from './card-history-model';

// «Movimientos de la tarjeta» — the PNR card's history (care-card history, 2026-10-30),
// under the card in the PNR drawer. The owner: «entré a un PNR, fui a la parte donde está la
// tarjeta y veo el total, pero no veo cuándo se le dio, cuánto ni por qué». Every load and
// adjustment, newest first: when (the airport's clock), how much, what (meal tier N, the
// case allowance, a manual top-up, spend, the redemption, the void), who (Nexa or the
// operator by name) and why (the policy's rule, or the operator's reason). Read from the
// wallet through operator-bff; kept live by the `CareChanged` push. Money is the card's
// face value (what the passenger can spend — the same amounts the card above shows).

export function CardHistory({
  disruptionUrn,
  subCaseUrn,
  caseUrn,
  originIata,
  currency,
}: {
  disruptionUrn: string;
  subCaseUrn: string;
  caseUrn: string;
  originIata?: string | null;
  currency: string;
}) {
  const { t } = useTranslation('admin', { keyPrefix: 'card.history' });
  const query = useCareHistory(disruptionUrn, subCaseUrn, caseUrn);
  const history = query.data;
  const cur = history?.card?.currency ?? currency;
  const money = (minor: number): string => formatMinor(minor, cur);

  if (query.isLoading) {
    return <Skeleton className="mt-3 h-16 w-full" />;
  }
  // An older BFF without the route (404 → null): say nothing rather than a false «sin movimientos».
  if (history === null || history === undefined) return null;

  const entries = history.entries;
  return (
    <section className="mt-4 border-t border-border pt-3" aria-labelledby="card-history-title" data-testid="card-history">
      <h3
        id="card-history-title"
        className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
      >
        <History className="h-3.5 w-3.5" aria-hidden />
        {t('title.section')}
      </h3>
      {query.isError || !history.known ? (
        <p className="text-xs text-muted-foreground" data-testid="card-history-unread">
          {t('unread')}
        </p>
      ) : history.entries.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="card-history-empty">
          {t('empty')}
        </p>
      ) : (
        <>
          <ol className="flex max-h-96 flex-col divide-y divide-border overflow-y-auto">
            {entries.map((e) => {
              const amount = signedAmount(e, money);
              const why = entryWhy(e, t, money, (iso) => formatFlightTime(iso, originIata));
              return (
                <li key={e.id} className="flex gap-3 py-2 text-xs" data-testid="card-history-entry" data-kind={e.kind}>
                  <time dateTime={e.at} className="w-24 shrink-0 font-mono tabular-nums text-muted-foreground">
                    {formatFlightTime(e.at, originIata)}
                  </time>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-baseline justify-between gap-x-2">
                      <span className="font-semibold">{entryTitle(e, t)}</span>
                      {amount ? (
                        <b
                          className={cn(
                            'font-mono tabular-nums',
                            (e.status === 'FAILED' || e.status === 'SKIPPED') && 'text-muted-foreground line-through',
                            e.duplicateOfAt && 'text-amber-700 dark:text-amber-300',
                            e.direction === 'DEBIT' ? 'text-foreground' : 'text-emerald-700 dark:text-emerald-300',
                          )}
                          data-testid="card-history-amount"
                        >
                          {amount}
                        </b>
                      ) : null}
                    </p>
                    <p className="text-muted-foreground" data-testid="card-history-who">
                      {actorLabel(e.actor, t)}
                    </p>
                    {why.map((line) => (
                      <p
                        key={line}
                        className={cn(
                          'text-muted-foreground',
                          e.status === 'FAILED' && line === why[why.length - 1] && 'text-rose-700 dark:text-rose-300',
                          e.duplicateOfAt && line === why[why.length - 1] && 'font-medium text-amber-800 dark:text-amber-300',
                        )}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </section>
  );
}
